import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { relativeDate, ticketTitle } from '@/lib/taskRules'
import { AuditFeed } from './AuditFeed'
import { EmptyNote, RowLink, RowSkeleton, Section, StatTile, TileGrid } from './shared'
import type { useTaskBuckets } from './useTaskBuckets'

type Buckets = ReturnType<typeof useTaskBuckets>

/**
 * QA home. `isVisibleKind` already narrows a qa user's tasks to `qa_check`
 * alone, so every bucket here is QA sample work by construction.
 *
 * qa holds `audit:read` but **not** `task:review`, so there is no
 * `GET /ingest/needs-review` query on this page — that would 403 every poll.
 */
export function QaHome({ buckets }: { buckets: Buckets }) {
  const { inProgress, available, isLoading } = buckets

  return (
    <>
      <Section
        title="My QA samples"
        description="QA checks you've claimed. Each one asks a pass/fail verdict on an existing annotation, in the read-only viewer."
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/work?tab=progress">Open in progress</Link>
          </Button>
        }
      >
        {isLoading ? (
          <RowSkeleton rows={2} />
        ) : inProgress.length === 0 ? (
          <EmptyNote>
            No QA checks in progress. Samples are drawn by a maintainer from a collection — claim
            one from{' '}
            <Link to="/work?tab=available" className="underline underline-offset-2">
              Available
            </Link>{' '}
            when they appear.
          </EmptyNote>
        ) : (
          <div className="space-y-2">
            {inProgress.slice(0, 5).map((t) => (
              <RowLink
                key={t.id}
                to={`/work?tab=progress&task=${t.id}`}
                primary={ticketTitle(t)}
                secondary={<span className="font-mono">{t.study_uid}</span>}
                right={relativeDate(t.updated_at)}
              />
            ))}
          </div>
        )}
      </Section>

      <Section title="Sampling" description="Each number opens the list it counts.">
        <TileGrid>
          <StatTile
            to="/work?tab=available"
            label="QA samples to claim"
            value={available.length}
            hint="Unclaimed QA checks. Claiming one holds it while you review."
            emptyHint="No QA samples waiting. They appear when a maintainer runs sampling on a collection."
            loading={isLoading}
          />
          <StatTile
            to="/activity"
            label="Checks in progress"
            value={inProgress.length}
            hint="Claimed and not yet submitted. Your full history is on Activity."
            emptyHint="Nothing claimed. Your verdict history stays on Activity."
            loading={isLoading}
          />
        </TileGrid>
      </Section>

      <Section
        title="Overrides"
        description="A maintainer approved a study that ingestion QC had held. Worth a second look."
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/audit">Open audit log</Link>
          </Button>
        }
      >
        <AuditFeed
          action="ingestion.qc_override"
          emptyCopy="No QC overrides recorded. Every study that reached the pipeline passed ingestion QC on its own."
        />
      </Section>

      <Section
        title="Quarantines"
        description="Studies pulled out of the pipeline by ingestion. Nothing downstream ever ran on them."
      >
        <AuditFeed
          action="ingestion.quarantine"
          emptyCopy="Nothing has been quarantined. Ingestion quarantines a study when QC fails and the reviewer rejects it."
        />
      </Section>
    </>
  )
}
